// University of Massachusetts Amherst: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UMass Amherst's Common Data Set 2025-2026 (common_data_set.pdf, the
//    "Fall 2025 Common Data Set" file University Analytics and Institutional
//    Research links from its Common Data Set page): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy, C21/C22
//    plans. Its G1/G5 costs are for 2025-2026, so the cost table uses the
//    financial aid office's 2026-2027 figures instead.
//  - UAIR's Admissions and Enrollment Report, Fall 2025 (fall25.pdf),
//    Admissions Table 1: first-year applications and acceptances by school or
//    college of the first-choice major. Its first-year totals (53,047 applied,
//    31,758 accepted in a first-choice or alternate major) differ slightly from
//    the CDS, and the admissions office's statistics page shows 53,091
//    applications; the page uses the CDS for the headline rate.
//  - UMass Amherst Undergraduate Admissions: Important Dates and Deadlines,
//    The Admission Decision, Early Action FAQ, Test-Optional Policy, Special
//    Requirements for Selective Majors. None of these pages carries a year;
//    they are the pages as posted for the application that opened August 1,
//    2026 (Fall 2027 entry). The Important Dates page is the next-cycle check:
//    it lists only the current cycle's dates and no fall 2028 change. Quirk: the decision pages give Regular Decision
//    release as "by the end of March" in one place and "early March" in
//    another; the page says March.
//  - UMass Amherst Financial Aid, Undergraduate Costs, 2026-2027 (9-month),
//    full-time undergraduate, in-state and out-of-state on or off campus. The
//    office calls these estimates, not final until the Board of Trustees
//    approves them. Its items sum exactly to its totals ($39,896 and $63,983).
//  - College Scorecard, school id 166629 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UMass Amherst Common Data Set 2025-2026',
        url: 'https://www.umass.edu/uair/data/common-data-set',
        read: '2026-10-01',
    },
    byCollege: {
        label: 'UMass Amherst UAIR, Admissions and Enrollment Report, Fall 2025',
        url: 'https://www.umass.edu/uair/media/1000/download',
        read: '2026-10-01',
    },
    dates: {
        label: 'UMass Amherst Admissions, Important Dates and Deadlines',
        url: 'https://www.umass.edu/admissions/important-dates-deadlines',
        read: '2026-10-01',
    },
    decision: {
        label: 'UMass Amherst Admissions, The Admission Decision',
        url: 'https://www.umass.edu/admissions/apply/admission-decision',
        read: '2026-10-01',
    },
    earlyAction: {
        label: 'UMass Amherst Admissions, Early Action FAQ',
        url: 'https://www.umass.edu/admissions/early-action-faq',
        read: '2026-10-01',
    },
    tests: {
        label: 'UMass Amherst Admissions, Test-Optional Policy',
        url: 'https://www.umass.edu/admissions/test-optional-policy',
        read: '2026-10-01',
    },
    specialMajors: {
        label: 'UMass Amherst Admissions, Special Requirements for Selective Majors',
        url: 'https://www.umass.edu/admissions/special-requirements-certain-majors',
        read: '2026-10-01',
    },
    costs: {
        label: 'UMass Amherst Financial Aid, Undergraduate Costs',
        url: 'https://www.umass.edu/financialaid/undergraduate-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?166629-University-of-Massachusetts-Amherst',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-massachusetts-amherst',
    kbId: 'university_of_massachusetts_amherst',
    name: 'University of Massachusetts Amherst',
    shortName: 'UMass Amherst',
    aliases: ['UMass'],
    location: 'Amherst, MA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UMass brand colors page); decoration only.
    branding: {
        primary: '#840028',
        secondary: '#212721',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_massachusetts_amherst_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 53117,
        admitted: 31813,
        enrolled: 5315,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 22603, admitted: 13062 },
            { group: 'Out-of-state', applied: 23800, admitted: 15117 },
            { group: 'International', applied: 6714, admitted: 3634 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2025',
        published: true,
        note: 'You name an intended major when you apply. UMass Amherst\'s institutional research office reports first-year applications and acceptances by the school or college of each applicant\'s first-choice major; the rates below are acceptances into that first-choice major. Some applicants are offered an alternate major instead, so the report\'s overall first-year acceptance (31,758 of 53,047) is higher than the sum of these rows, and its totals differ slightly from the Common Data Set\'s. "Other programs" is the report\'s own line.',
        head: ['School or college', 'Applied', 'Admitted to first-choice major', 'Rate'],
        rows: [
            { name: 'College of Humanities and Fine Arts', applied: 3806, admitted: 2343, rate: 61.6 },
            { name: 'College of Information and Computer Sciences', applied: 4991, admitted: 2362, rate: 47.3 },
            { name: 'College of Natural Sciences', applied: 13675, admitted: 9832, rate: 71.9 },
            { name: 'College of Social and Behavioral Sciences', applied: 5986, admitted: 4363, rate: 72.9 },
            { name: 'College of Education', applied: 999, admitted: 681, rate: 68.2 },
            { name: 'College of Engineering', applied: 7099, admitted: 4205, rate: 59.2 },
            { name: 'School of Management (Isenberg)', applied: 10485, admitted: 4020, rate: 38.3 },
            { name: 'College of Nursing', applied: 2703, admitted: 367, rate: 13.6 },
            { name: 'School of Public Health and Health Sciences', applied: 2654, admitted: 1932, rate: 72.8 },
            { name: 'Other programs', applied: 649, admitted: 463, rate: 71.3 },
        ],
        source: 'byCollege',
        where: 'Admissions Table 1, Undergraduate Admissions, Fall Semester 2025, first-year rows',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UMass Amherst has no early decision plan. Its early action plan is not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted in early action, so it gives no early acceptance rate. UMass Amherst\'s Early Action FAQ says the early action admit rate is slightly higher than Regular Decision\'s because early applicants tend to have stronger academic records.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply with an intended major, and some majors are more selective or ask for more:',
        rows: [
            { school: 'Isenberg School of Management (BBA)', requirement: 'Selective. You can pick business on the Common App, but business is not a major: if admitted you enter the BBA program undeclared. You need a strong academic record in math through pre-calculus.' },
            { school: 'Computer Science', requirement: 'Highly selective. Applicants with a strong academic record in math through pre-calculus are given priority.' },
            { school: 'Riccio College of Engineering', requirement: 'Selective, with a strong record in math and science, including chemistry and physics. You are admitted to the college and choose a specific engineering major at the end of your first year.' },
            { school: 'Nursing', requirement: 'Highly selective and open only to first-year applicants. If you are not admitted to nursing, you cannot change your major to nursing after you enroll.' },
            { school: 'Sport Management', requirement: 'Based on your overall academic record and sport management related activities, with a strong record in math through pre-calculus.' },
            { school: 'Architecture and Art', requirement: 'Apply to Undergraduate Admissions and send a portfolio directly to the department.' },
            { school: 'Music and Dance', requirement: 'Apply to Undergraduate Admissions and arrange an audition with the Department of Music and Dance, scheduled three weeks before the audition date.' },
        ],
        note: 'Music and dance audition dates and art and architecture portfolio reviews can accommodate Early Action applicants.',
        source: 'specialMajors',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 5', decision: 'By the end of January', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March', binding: false },
        ],
        note: 'The application and supporting documents are both due by the deadline. Send the FAFSA by February 15 to meet the March 1 priority deadline; the candidate reply date is May 1. UMass Amherst gives Regular Decision release as "by the end of March" in one place on this page and "early March" in another.',
        source: 'decision',
        binding: 'Early Action is not binding: you can apply to other colleges and have until May 1 to decide. It is the only early plan UMass Amherst offers. If you are denied in Early Action, you are not reconsidered in Regular Decision.',
        bindingSource: 'earlyAction',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'The SAT and ACT are optional for first-year applicants to every major. If you send scores, they are part of the review, and UMass Amherst superscores, using your best combination across all your tests. Scores must come directly from the testing agency. Without scores, your GPA plays a bigger part in the review.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [19212, 43299] },
            { item: 'Housing (average)', amounts: [9772, 9772] },
            { item: 'Food', amounts: [8272, 8272] },
            { item: 'Books', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [400, 400] },
            { item: 'Miscellaneous', amounts: [1000, 1000] },
            { item: 'Average federal loan fees', amounts: [40, 40] },
        ],
        totals: [39896, 63983],
        note: 'For a full-time undergraduate for the 9-month year, on or off campus. UMass Amherst calls these estimates, not final until approved by the Board of Trustees. Some majors, Commonwealth Honors College and first-term enrollment add fees, and the Student Health Benefit Plan is not included.',
        source: 'costs',
        netPrice: {
            average: 22857,
            byIncome: [
                { income: '$0 to $30,000', amount: 10294 },
                { income: '$30,001 to $48,000', amount: 10343 },
                { income: '$48,001 to $75,000', amount: 12020 },
                { income: '$75,001 to $110,000', amount: 17769 },
                { income: 'Over $110,000', amount: 33795 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UMass Amherst\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
